/*
********************************************************************************
Functions related to page validation
********************************************************************************
*/
import { isVisible, consoleMsg } from "./utility.js";
import { generateDamageImage, captureLogo } from "./damage-map.js";
import { legendImg, logoImg900 } from "./images.js";
import { showDiv, hideDiv, handleSubmit, next } from "./navigation.js";
import { buildDamageIssues } from "./storage.js";

export { validatePage };

/* Previous page validation logic was getting unwieldy, as additional use cases evolved and were
merged into the implementation.  New approach refactors by exposing a validator object,
comprised of individual arrays of discrete tests for each page.  As additional vendors are added,
or additional validation checks are required, this modular approach is easily extensible by defining a 
new page array or named validation check. */

const validators = {
	// Inspection Details
	page1: [
		{
			// Generic required-input sweep. validateInputs skips starting-mileage (own validator below)
			// and license-plate-number (optional — a vehicle may have no plate fitted).
			name: "ion-input",
			validate: async () => {
				const ionInputs = document.querySelectorAll("ion-input");
				const { isValid, firstInvalidElement } = await validateInputs(ionInputs);
				return { isValid, firstInvalidElement };
			},
		},
		{
			name: "starting-mileage",
			validate: async () => {
				const input = document.querySelector("ion-input#starting-mileage");
				if (!input) return { isValid: true, firstInvalidElement: null };

				const nativeInput = input.shadowRoot ? input.shadowRoot.querySelector("input") : input.querySelector("input");

				// Add focus event listener to clear errors when user clicks into field
				if (!input.hasAttribute("data-focus-listener")) {
					input.setAttribute("data-focus-listener", "true");
					if (nativeInput) {
						nativeInput.addEventListener("focus", () => {
							const original = input.getAttribute("data-original-placeholder");
							if (original != null) {
								input.setAttribute("placeholder", original);
								input.removeAttribute("data-original-placeholder");
								nativeInput.classList.remove("native-error");
								nativeInput.setAttribute("placeholder", original);
							}
						});
					}
				}

				const getValue = () => {
					if (nativeInput && typeof nativeInput.value === "string") return nativeInput.value;
					return input.value || "";
				};

				const value = getValue();

				const setError = (msg, clearField) => {
					if (clearField) {
						input.value = "";
						if (nativeInput) {
							nativeInput.value = "";
						}
					}
					const original = input.getAttribute("data-original-placeholder") || input.getAttribute("placeholder") || "";
					input.setAttribute("data-original-placeholder", original);
					input.setAttribute("placeholder", msg);
					if (nativeInput) {
						nativeInput.classList.add("native-error");
						nativeInput.setAttribute("placeholder", msg);
					}
				};

				const clearError = () => {
					const original = input.getAttribute("data-original-placeholder");
					if (original != null) {
						input.setAttribute("placeholder", original);
						input.removeAttribute("data-original-placeholder");
					}
					if (nativeInput) {
						nativeInput.classList.remove("native-error");
						nativeInput.setAttribute("placeholder", input.getAttribute("placeholder") || "");
					}
				};

				// Simple validation logic - treat spaces as empty
				const trimmed = value.trim();
				const num = Number(trimmed);
				const isValid = trimmed !== "" && !isNaN(num) && num >= 0 && num <= 200000;

				if (!isValid) {
					// Handle high values with alert (prevent multiple alerts)
					if (!isNaN(num) && num > 200000) {
						if (!document.querySelector("ion-alert[data-mileage-alert]")) {
							const alert = document.createElement("ion-alert");
							alert.setAttribute("data-mileage-alert", "true");
							alert.header = "Check Starting Mileage";
							alert.message = "Starting mileage seems unusually high. Is this correct?";
							alert.buttons = [
								{
									text: "OK",
									handler: () => {
										alert.remove();
									},
								},
							];
							document.body.appendChild(alert);
							await alert.componentOnReady();
							await alert.present();
							alert.addEventListener(
								"ionAlertDidDismiss",
								() => {
									alert.remove();
								},
								{ once: true },
							);
						}
						return { isValid: false, firstInvalidElement: input };
					}

					// All other errors get the same message and clear the field
					const shouldClear = trimmed !== ""; // Clear field if not empty (including spaces)
					setError("Positive number required", shouldClear);
					return { isValid: false, firstInvalidElement: input };
				}

				/*
				Valid — clear errors. The value is NOT written back into dataTables: that
				stays a read-only snapshot of what NAVS passed, so it remains a stable
				baseline for the submission delta in buildSubmissionData. handleSubmit
				persists the entered value to page1 storage.
				*/
				clearError();
				return { isValid: true, firstInvalidElement: null };
			},
		},
	],
	// Exterior inspection
	page2: [
		{
			name: "ion-radio-group",
			validate: async () => {
				const groups = document.querySelectorAll("ion-radio-group");
				const { isValid, firstInvalidElement } = await validateRadios(groups);
				return { isValid, firstInvalidElement };
			},
		},
		{
			name: "ion-text-area",
			validate: async () => {
				const textareas = document.querySelectorAll("ion-textarea");
				const isValid = validateTextAreas(textareas);
				return { isValid, firstInvalidElement: isValid ? null : findFirstInvalidElement(textareas) };
			},
		},
		{
			name: "damage-photos",
			validate: async () => {
				const rows = document.querySelectorAll("ion-row");
				const isValid = await checkDamagePhotos(rows);
				return { isValid, firstInvalidElement: isValid ? null : findFirstInvalidElement(rows) };
			},
		},
	],
	// Glovebox Documents
	page3: [
		{
			name: "ion-radio-group",
			validate: async () => {
				const groups = document.querySelectorAll("ion-radio-group");
				const { isValid, firstInvalidElement } = await validateRadios(groups);
				return { isValid, firstInvalidElement };
			},
		},
	],
	// Interior Check (vendor = default)
	page4: [
		{
			name: "ion-radio-group",
			validate: async () => {
				const groups = document.querySelectorAll("ion-radio-group");
				const { isValid, firstInvalidElement } = await validateRadios(groups);
				return { isValid, firstInvalidElement };
			},
		},
		{
			name: "ion-text-area",
			validate: async () => {
				const textareas = document.querySelectorAll("ion-textarea");
				const isValid = validateTextAreas(textareas);
				return { isValid, firstInvalidElement: isValid ? null : findFirstInvalidElement(textareas) };
			},
		},
	],
	// Road Test (vendor = default)
	page5: [
		{
			name: "ion-radio-group",
			validate: async () => {
				const groups = document.querySelectorAll("ion-radio-group");
				const { isValid, firstInvalidElement } = await validateRadios(groups);
				return { isValid, firstInvalidElement };
			},
		},
		{
			name: "ion-text-area",
			validate: async () => {
				const textareas = document.querySelectorAll("ion-textarea");
				const isValid = validateTextAreas(textareas);
				return { isValid, firstInvalidElement: isValid ? null : findFirstInvalidElement(textareas) };
			},
		},
	],
	// Lights Check
	page6: [
		{
			name: "ion-radio-group",
			validate: async () => {
				const groups = document.querySelectorAll("ion-radio-group");
				const { isValid, firstInvalidElement } = await validateRadios(groups);
				return { isValid, firstInvalidElement };
			},
		},
		{
			name: "ion-text-area",
			validate: async () => {
				const textareas = document.querySelectorAll("ion-textarea");
				const isValid = validateTextAreas(textareas);
				return { isValid, firstInvalidElement: isValid ? null : findFirstInvalidElement(textareas) };
			},
		},
	],
	// Fluid Inspection
	page7: [
		{
			name: "ion-radio-group",
			validate: async () => {
				const groups = document.querySelectorAll("ion-radio-group");
				const { isValid, firstInvalidElement } = await validateRadios(groups);
				return { isValid, firstInvalidElement };
			},
		},
	],
	// Tire Inspection
	page8: [
		{
			name: "ion-radio-group",
			validate: async () => {
				const groups = document.querySelectorAll("ion-radio-group");
				const { isValid, firstInvalidElement } = await validateRadios(groups);
				return { isValid, firstInvalidElement };
			},
		},
		{
			name: "ion-input",
			validate: async () => {
				const ionInputs = document.querySelectorAll("ion-input");
				consoleMsg(`ionInputs = ${ionInputs}`);
				const { isValid, firstInvalidElement } = await validateInputs(ionInputs);
				//return { isValid, firstInvalidElement: isValid ? null : ionInputs };
				return { isValid, firstInvalidElement };
			},
		},
	],
	// Inspection Photos
	page9: [
		{
			name: "photos",
			validate: async () => {
				const isValid = await checkPhotos(app.state.currentPage);
				const firstInvalidElement = isValid ? null : findMissingPhoto(document.querySelector(".photo-container .error-overlay:not(.hidden)"));
				return { isValid, firstInvalidElement };
			},
		},
	],
	// Signature
	page10: [
		{
			name: "ion-radio-group",
			validate: async () => {
				const groups = document.querySelectorAll("ion-radio-group");
				const { isValid, firstInvalidElement } = await validateRadios(groups);
				return { isValid, firstInvalidElement };
			},
		},
		{
			name: "ending-mileage",
			validate: async () => {
				const input = document.querySelector("ion-input#ending-mileage");
				if (!input) return { isValid: true, firstInvalidElement: null };

				const nativeInput = input.shadowRoot ? input.shadowRoot.querySelector("input") : input.querySelector("input");

				// Add focus event listener to clear errors when user clicks into field
				if (!input.hasAttribute("data-focus-listener")) {
					input.setAttribute("data-focus-listener", "true");
					if (nativeInput) {
						nativeInput.addEventListener("focus", () => {
							const original = input.getAttribute("data-original-placeholder");
							if (original != null) {
								input.setAttribute("placeholder", original);
								input.removeAttribute("data-original-placeholder");
								nativeInput.classList.remove("native-error");
								nativeInput.setAttribute("placeholder", original);
							}
						});
					}
				}

				const value = input.value || "";
				const trimmed = value.trim();
				const endingMileage = Number(trimmed);

				consoleMsg(`Ending mileage validation - value: "${value}", trimmed: "${trimmed}", number: ${endingMileage}`);

				const setError = (msg, clearField) => {
					if (clearField) {
						input.value = "";
						if (nativeInput) {
							nativeInput.value = "";
							nativeInput.dispatchEvent(new Event("input", { bubbles: true }));
						}
					}
					const original = input.getAttribute("data-original-placeholder") || input.getAttribute("placeholder") || "";
					input.setAttribute("data-original-placeholder", original);
					input.setAttribute("placeholder", msg);
					if (nativeInput) {
						nativeInput.classList.add("native-error");
						nativeInput.setAttribute("placeholder", msg);
					}
				};

				const clearError = () => {
					const original = input.getAttribute("data-original-placeholder");
					if (original) {
						input.setAttribute("placeholder", original);
						input.removeAttribute("data-original-placeholder");
					}
					if (nativeInput) {
						nativeInput.classList.remove("native-error");
						nativeInput.setAttribute("placeholder", input.getAttribute("placeholder") || "");
					}
				};

				// Get starting mileage for comparison
				const startingMileage = Number(app.data.dataTables?.["vehicle-info-table"]?.["Starting Mileage"]) || 0;

				// Basic validation - check for empty or invalid input
				if (trimmed === "" || isNaN(endingMileage) || endingMileage < 0) {
					setError("Enter a positive number value greater than starting mileage", trimmed !== "");
					return { isValid: false, firstInvalidElement: input };
				}

				// Compare with starting mileage
				consoleMsg(`Starting mileage: ${startingMileage}, Ending mileage: ${endingMileage}`);
				if (endingMileage < startingMileage) {
					consoleMsg(`Validation failed: ending (${endingMileage}) < starting (${startingMileage})`);
					setError("Error: ending mileage is less than starting", true);
					return { isValid: false, firstInvalidElement: input };
				}

				clearError();
				return { isValid: true, firstInvalidElement: null };
			},
		},
		{
			name: "ion-input",
			validate: async () => {
				const ionInputs = document.querySelectorAll("ion-input");
				consoleMsg(`ionInputs = ${ionInputs}`);
				const { isValid, firstInvalidElement } = await validateInputs(ionInputs);
				//return { isValid, firstInvalidElement: isValid ? null : ionInputs };
				return { isValid, firstInvalidElement };
			},
		},
	],
	// Interior Inspection
	page11: [
		{
			name: "ion-radio-group",
			validate: async () => {
				const groups = document.querySelectorAll("ion-radio-group");
				const { isValid, firstInvalidElement } = await validateRadios(groups);
				return { isValid, firstInvalidElement };
			},
		},
		{
			name: "ion-text-area",
			validate: async () => {
				const textareas = document.querySelectorAll("ion-textarea");
				const isValid = validateTextAreas(textareas);
				return { isValid, firstInvalidElement: isValid ? null : findFirstInvalidElement(textareas) };
			},
		},
		{
			name: "damage-photos",
			validate: async () => {
				const rows = document.querySelectorAll("ion-row");
				const isValid = await checkDamagePhotos(rows);
				return { isValid, firstInvalidElement: isValid ? null : findFirstInvalidElement(rows) };
			},
		},
	],
	// MB Road Test
	page12: [
		{
			name: "ion-radio-group",
			validate: async () => {
				const groups = document.querySelectorAll("ion-radio-group");
				const { isValid, firstInvalidElement } = await validateRadios(groups);
				return { isValid, firstInvalidElement };
			},
		},
		{
			name: "ion-text-area",
			validate: async () => {
				const textareas = document.querySelectorAll("ion-textarea");
				const isValid = validateTextAreas(textareas);
				return { isValid, firstInvalidElement: isValid ? null : findFirstInvalidElement(textareas) };
			},
		},
	],
	// MB Communication Check
	page13: [
		{
			name: "ion-radio-group",
			validate: async () => {
				const groups = document.querySelectorAll("ion-radio-group");
				const { isValid, firstInvalidElement } = await validateRadios(groups);
				return { isValid, firstInvalidElement };
			},
		},
		{
			name: "ion-text-area",
			validate: async () => {
				const textareas = document.querySelectorAll("ion-textarea");
				const isValid = validateTextAreas(textareas);
				return { isValid, firstInvalidElement: isValid ? null : findFirstInvalidElement(textareas) };
			},
		},
	],
	// MB Function Check
	page14: [
		{
			name: "ion-radio-group",
			validate: async () => {
				const groups = document.querySelectorAll("ion-radio-group");
				const { isValid, firstInvalidElement } = await validateRadios(groups);
				return { isValid, firstInvalidElement };
			},
		},
		{
			name: "ion-text-area",
			validate: async () => {
				const textareas = document.querySelectorAll("ion-textarea");
				const isValid = validateTextAreas(textareas);
				return { isValid, firstInvalidElement: isValid ? null : findFirstInvalidElement(textareas) };
			},
		},
	],
	// MB Out Vehicle Settings
	page15: [
		{
			name: "ion-radio-group",
			validate: async () => {
				const groups = document.querySelectorAll("ion-radio-group");
				const { isValid, firstInvalidElement } = await validateRadios(groups);
				return { isValid, firstInvalidElement };
			},
		},
	],
	// MB Out Display Settings
	page16: [
		{
			name: "ion-radio-group",
			validate: async () => {
				const groups = document.querySelectorAll("ion-radio-group");
				const { isValid, firstInvalidElement } = await validateRadios(groups);
				return { isValid, firstInvalidElement };
			},
		},
	],
	// MB Out Upon Delivery
	page17: [
		{
			name: "ion-radio-group",
			validate: async () => {
				const groups = document.querySelectorAll("ion-radio-group");
				const { isValid, firstInvalidElement } = await validateRadios(groups);
				return { isValid, firstInvalidElement };
			},
		},
	],
};

/**
 * Validates the current page and navigates to the next page if valid.
 * @param {string} page - The page to navigate to if validation passes.
 */

async function validatePage(page) {
	const currentPage = app.state.currentPage;
	const pageValidators = validators[currentPage] || [];

	let firstErrorElement = null;

	// Iterate through the validation checks assigned to each page
	for (const { name, validate } of pageValidators) {
		// Call the validate function for current page from validators object
		const { isValid, firstInvalidElement } = await validate();

		if (!isValid) {
			firstErrorElement = firstErrorElement || firstInvalidElement;
		}
	}

	if (!firstErrorElement) {
		// All validations passed
		consoleMsg("No errors...");
		app.validator.validatePage(currentPage, true);
		await handleSubmit(currentPage);

		// Page-specific logic
		if (currentPage === "page2") {
			const legend = legendImg;
			const logo = logoImg900;
			const page2DataObj = await store.get(`${app.state.storageKey}_${currentPage}`);

			await generateDamageImage(app.state.damageMarkers);
			await generateDamageImage(app.state.damageMarkers, "legendCanvas", legend);
			await captureLogo(logo);
			//await buildDamageIssues(page2DataObj.page2);
			await buildDamageIssues(page2DataObj);
		} else if (currentPage === "page11") {
			await generateDamageImage(app.state.interiorDamageMarkers, "damageCanvas2", "/assets/img/car-interior-1000.png");
		} else if (currentPage === "page3") {
			await presentGloveboxAlerts();
		}
		if (page) next(page);
	} else {
		// One or more validations failed
		consoleMsg("ERRORS ON PAGE!");
		app.validator.validatePage(currentPage, false);
		consoleMsg("Scrolling to:", firstErrorElement);
		firstErrorElement.scrollIntoView({ behavior: "smooth", block: "center" });
	}
}

// Helper functions

/* Glovebox Documents. Missing and expired are two separate advisories, each presented
once, so the inspector acknowledges each case on its own. Raised from validatePage after
the page validates and handleSubmit commits it, so the inspector sees this at the point
the data is committed — consistent with every other check, which hangs off the next
button. Navigating back is not a commit and stays silent. */

const gloveboxDocuments = [
	{ name: "monroney", label: "Monroney" },
	{ name: "incident", label: "Incident form" },
	{ name: "insurance", label: "Insurance card" },
	{ name: "registration", label: "Registration card" },
];

async function presentGloveboxAlerts() {
	const bullets = (items) => `<ul>${items.map((item) => `<li>${item}</li>`).join("")}</ul>`;

	const missing = gloveboxDocuments.filter((doc) => document.getElementById(`${doc.name}-group`)?.value === "Not OK").map((doc) => doc.label);

	/* Compared as YYYY-MM-DD strings: new Date("2026-09-08") is UTC midnight, which lands
	on the previous day in local time. A document expiring today is not yet expired. */
	const now = new Date();
	const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

	// Monroney and incident carry no date element, so they drop out here
	const expired = gloveboxDocuments
		.filter((doc) => {
			/** @type {any} */
			const dateEl = document.getElementById(`${doc.name}-date`);
			const value = dateEl?.value;
			return value && value.slice(0, 10) < today;
		})
		.map((doc) => doc.label);

	consoleMsg("Glovebox missing:", missing, "expired:", expired);

	if (missing.length) {
		await presentDocAlert(
			`You indicated this vehicle is missing the following required documentation: ${bullets(missing)}Please contact your account manager immediately to resolve these missing documents.`,
		);
	}

	if (expired.length) {
		await presentDocAlert(
			`You indicated this vehicle has the following expired documents: ${bullets(expired)}Please contact your account manager immediately to resolve these expired documents.`,
		);
	}
}

/**
 * Presents one Glovebox advisory and resolves once it is dismissed, so multiple
 * advisories present one after the other rather than on top of each other.
 *
 * A fresh element per advisory, matching the starting-mileage alert above: an
 * ion-alert that has been dismissed will not re-present, so reusing one element
 * for both messages silently drops the second.
 *
 * @param {string} message
 * @returns {Promise<void>}
 */

function presentDocAlert(message) {
	/** @type {any} */
	const alert = document.createElement("ion-alert");
	alert.header = "Contact Account Manager";
	alert.message = message;
	alert.buttons = [{ text: "OK", role: "cancel" }];
	document.body.appendChild(alert);

	return new Promise((resolve) => {
		alert.addEventListener(
			"ionAlertDidDismiss",
			() => {
				alert.remove();
				resolve();
			},
			{ once: true },
		);
		alert.componentOnReady().then(() => alert.present());
	});
}

function findFirstInvalidElement(elements) {
	consoleMsg("Finding first invalid element...");
	for (const element of elements) {
		if (!element.value) {
			consoleMsg("First invalid element found: ", element);
			return element;
		}
	}
	return null;
}

function findMissingPhoto() {
	consoleMsg("Finding first missing photo...");
	const missing = document.querySelector(".photo-container .error-overlay:not(.hidden)");
	if (missing) {
		consoleMsg("First invalid element found: ", missing);
		return missing;
	}
	return null;
}

/**
 * Iterates through all text areas on current page.  If visible, checks that the user
 * has actually entered text.  If not, display an error div.
 *
 * @param {array} array
 * @returns {void}
 */

async function checkText(array) {
	var textInput = false;
	var errorObj = {};

	// iterate through all text areas in array
	for (var i = 0; i < array.length; i++) {
		// assign current textarea to a variable
		var ta = array[i];
		var taId = ta.parentElement.id;
		var errorDiv = ta.parentElement.id + "-error";

		// Reset each error div to hidden to clear previous check

		hideDiv(errorDiv);
		// Iterate through all visible textareas to find ones that are empty
		if (!ta.parentElement.classList.contains("hidden")) {
			consoleMsg("Parent id = " + ta.parentElement.id);
			if (ta.value == "") {
				showDiv(errorDiv);
				errorObj[taId] = taId;
				// Check if element has been previously added to local storage and, if so, remove
				/*if(localStorage.hasOwnProperty(taId)){
                     localStorage.removeItem(taId);
                 }*/
				consoleMsg("Error object length = " + Object.keys(errorObj).length);
			} else {
				delete errorObj[taId];
				consoleMsg("Error object length = " + Object.keys(errorObj).length);
				/*localStorage.setItem(taId, ta.value);
                 
                 consoleMsg(taId + " val = " + ta.value);
                 consoleMsg("Error object length = " + Object.keys(errorObj).length);	*/
			}
		}
	}
	if (Object.keys(errorObj).length === 0) {
		textInput = true;
	}
	//return errorObj;
	return textInput;
}

async function validateInputs(inputs) {
	let errors = 0;
	let firstInvalidElement = null;
	let isValid;

	for (let input of inputs) {
		// Skip starting-mileage and ending-mileage inputs since they have their own validators.
		// license-plate-number is skipped because it is optional: a vehicle can legitimately
		// be inspected with no plate fitted (per Melissa, 2026-09-10).
		if (input.id === "starting-mileage" || input.id === "ending-mileage" || input.id === "license-plate-number") {
			continue;
		}

		const value = input?.value?.trim();
		let message = "This field is required"; // Changed this line

		// Get the internal native input (light DOM or shadow DOM fallback)
		const nativeInput = input.shadowRoot ? input.shadowRoot.querySelector("input") : input.querySelector("input");

		if (!input.value) {
			errors += 1;
			consoleMsg(`Missing input errors = ${errors}`);
			firstInvalidElement = firstInvalidElement || input;
			// Save and override placeholder on the ion-input (optional, still works)
			const original = input.getAttribute("placeholder");
			input.setAttribute("data-original-placeholder", original);
			input.setAttribute("placeholder", message);

			// Apply error styling to the actual input field
			if (nativeInput) {
				nativeInput.classList.add("native-error");
				nativeInput.setAttribute("placeholder", message); // Changed this line too
			}
		} else {
			// Reset ion-input placeholder
			const original = input.getAttribute("data-original-placeholder");
			if (original) {
				input.setAttribute("placeholder", original);
				input.removeAttribute("data-original-placeholder");
			}
			input.classList.remove("placeholder-error");

			// Remove native input styling
			if (nativeInput) {
				nativeInput.classList.remove("native-error");
				nativeInput.setAttribute("placeholder", input.getAttribute("placeholder") || "");
			}
		}
	}
	isValid = errors === 0;
	return { isValid, firstInvalidElement };
}

async function validateRadios(groups) {
	let errors = 0;
	let firstInvalidElement = null;

	for (const group of groups) {
		if (!group.value) {
			showDiv(group.name + "-error");
			errors += 1;
			firstInvalidElement = firstInvalidElement || group;
		} else {
			hideDiv(group.name + "-error");
		}
	}

	const isValid = errors === 0;
	return { isValid, firstInvalidElement };
}

async function validateTextAreas(areas) {
	let errors = 0;
	for (const area of areas) {
		if (isVisible(area)) {
			const errorDiv = area.parentElement.id + "-error";
			if (!area.value) {
				consoleMsg(area.id + " has no value");
				showDiv(errorDiv);
				errors += 1;
			} else {
				hideDiv(errorDiv);
			}
		}
	}
	const isValid = errors === 0;
	const firstInvalidElement = isValid ? null : findFirstInvalidElement(areas);
	return { isValid, firstInvalidElement };
}

// Validate Starting Mileage input value
function validateStartingMileage(value) {
	const trimmed = String(value || "").trim();
	console.log("DEBUG validateStartingMileage got:", value, "trimmed:", trimmed);

	if (trimmed === "") {
		return { valid: false, type: "empty", message: "This field is required." };
	}

	// First check if it's a negative number
	if (trimmed.startsWith("-")) {
		const num = Number(trimmed);
		if (!isNaN(num)) {
			return { valid: false, type: "negative", message: "Positive number required" };
		}
	}

	// Check for non-numeric characters (allow decimals but not negative)
	if (!/^\d*\.?\d*$/.test(trimmed)) {
		return { valid: false, type: "non-numeric", message: "Numeric value required" };
	}

	const num = Number(trimmed);
	console.log("DEBUG parsed number:", num, "isNaN?", isNaN(num));

	if (isNaN(num) || !isFinite(num)) {
		return { valid: false, type: "invalid", message: "Invalid number format" };
	}

	if (num > 200000) {
		return { valid: false, type: "high", message: "Starting mileage seems unusually high. Is this correct?" };
	}

	return { valid: true, type: "valid", value: num };
}

async function checkPhotos(currentPage) {
	let valid;
	let errors = 0;
	if (currentPage !== "page9") {
		return (valid = true);
	}
	const keys = Object.keys(app.state.photoData);
	for (let i = 0; i < keys.length; i++) {
		const key = keys[i];
		const div = document.getElementById(key);
		const errorDiv = document.getElementById(key + "-error");
		const validIcon = document.getElementById(key + "-valid-icon");
		if (app.state.photoData[key].updated !== true) {
			//div.classList.add("hidden");
			errorDiv.classList.remove("hidden");
			validIcon.classList.add("hidden");
			errors++;
		} else {
			//div.classList.remove("hidden");
			errorDiv.classList.add("hidden");
			validIcon.classList.remove("hidden");
		}
	}
	if (errors === 0) {
		valid = true;
	}
	consoleMsg("NEW Inspection photos error count: ", errors);
	consoleMsg("checkPhotos function called...");
	return valid;
}

async function checkDamagePhotos(photoRows) {
	let valid;
	let errors = 0;
	photoRows.forEach((row) => {
		if (isVisible(row)) {
			let errorDiv = row.parentElement.parentElement.id + "-photo-error";
			if (row.children.length === 0) {
				consoleMsg("No photo taken");
				consoleMsg("Error div = ", errorDiv);
				showDiv(errorDiv);
				errors += 1;
			} else {
				consoleMsg("Photo taken");
				hideDiv(errorDiv);
			}
		}
	});
	if (errors === 0) {
		valid = true;
	}
	return valid;
}
